import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageMeta from './PageMeta';
import { useTranslation } from '../contexts/LanguageContext';
import { formatDate } from '../i18n';
import './LegalDocument.css';

function PrivacyPolicy() {
    const navigate = useNavigate();
    const { t, language } = useTranslation();
    const title = t('legal.privacy.title');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="legal-document">
            <PageMeta path="/privacy" />
            <div className="legal-container">
                <button className="legal-back-btn" onClick={() => navigate('/')}>
                    <i className="fas fa-arrow-left"></i> {t('legal.backToGame')}
                </button>
                <h1>{title}</h1>
                <p className="last-updated">{t('legal.lastUpdated', { date: formatDate(new Date(2026, 8, 29), { dateStyle: 'long' }, language) })}</p>
                {language !== 'en' && (
                    <div className="contact-info" role="note">
                        <p>{t('legal.englishOnly')}</p>
                    </div>
                )}

                <section>
                    <h2>1. Data Controller</h2>
                    <p>
                        The data controller responsible for your personal data is:
                    </p>
                    <div className="contact-info">
                        <p><strong>Company:</strong> CoreDL d.o.o.</p>
                        <p><strong>OIB:</strong> 33905700350</p>
                        <p><strong>Address:</strong> Gajnički vidikovac II. 14/2, 10090 Zagreb, Croatia</p>
                        <p><strong>Email:</strong> support@word-labyrinth.com</p>
                    </div>
                    <p>
                        We operate Word Labyrinth, a word puzzle game available on our website and mobile
                        applications (iOS and Android) (collectively, the "Service").
                    </p>
                    <p>
                        As a small-scale data processor, we are not required to appoint a Data Protection Officer
                        under GDPR Article 37. For all privacy-related inquiries, please contact us
                        at <a href="mailto:support@word-labyrinth.com">support@word-labyrinth.com</a>.
                    </p>
                </section>

                <section>
                    <h2>2. Information We Collect</h2>

                    <h3>2.1 Information You Provide to Us</h3>
                    <ul>
                        <li><strong>Account Information:</strong> When you create an account, we collect your email address,
                        username, and password (stored encrypted).</li>
                        <li><strong>Social Media Authentication:</strong> If you sign in using Google, Facebook, Apple, or
                        X/Twitter, we receive basic profile information such as your name, email address, and profile picture
                        from these services.</li>
                        <li><strong>Payment Information:</strong> When you make purchases, payment information is processed
                        securely by our payment processors (Stripe, Apple App Store, Google Play Store). We do not store
                        complete credit card numbers on our servers. We store transaction records including amount, currency,
                        payment provider, and your country (for VAT purposes).</li>
                        <li><strong>Communications:</strong> If you contact us for support or feedback, we collect the
                        information you provide in your messages.</li>
                    </ul>

                    <h3>2.2 Information Collected Automatically</h3>
                    <ul>
                        <li><strong>Game Progress:</strong> Your game statistics, levels completed, scores, leaderboard
                        rankings, and hint usage.</li>
                        <li><strong>Device Information:</strong> Device type, operating system, browser type, and mobile
                        network information.</li>
                        <li><strong>IP Address:</strong> Collected for security, fraud prevention, and to determine your
                        country for VAT compliance (two pieces of evidence rule under EU VAT rules).</li>
                        <li><strong>Usage Data:</strong> Information about how you interact with our game, including
                        features used, time spent playing, and in-game actions.</li>
                        <li><strong>Push Notification Tokens:</strong> If you enable push notifications, we store your
                        device token or web push subscription endpoint.</li>
                    </ul>

                    <h3>2.3 Cookies and Similar Technologies</h3>
                    <p>We use the following cookies:</p>
                    <ul>
                        <li><strong>Strictly Necessary Cookies (no consent required):</strong>
                            <ul>
                                <li><code>sessionid</code>: Django session cookie for authentication (30 days)</li>
                                <li><code>csrftoken</code>: CSRF protection token (session)</li>
                                <li><code>wl_client_id</code>: anonymous player identification UUID, used to maintain
                                your game progress before account creation (persistent)</li>
                                <li><code>cookie_consent</code>: records your cookie preferences (1 year)</li>
                            </ul>
                        </li>
                        <li><strong>Self-Hosted Analytics (no consent required):</strong>
                            <ul>
                                <li>We use a self-hosted instance of <a href="https://plausible.io" target="_blank" rel="noopener noreferrer">Plausible Analytics</a> to
                                measure aggregate site usage (page views, custom events). Plausible does not use cookies, does not
                                collect personal data, does not track you across websites, and does not create user profiles. All
                                data is processed on our own servers within the EU. This processing is based on our legitimate
                                interest (Art. 6(1)(f) GDPR) in understanding how the Service is used. See <a href="https://plausible.io/data-policy" target="_blank" rel="noopener noreferrer">Plausible's data policy</a> for
                                technical details.</li>
                            </ul>
                        </li>
                        <li><strong>Analytics &amp; Advertising Cookies (consent required):</strong>
                            <ul>
                                <li>Google Analytics (GA4): used to measure site usage and improve the Service.
                                See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a> for details.</li>
                                <li>Google AdSense / AdMob cookies: used to serve and measure advertisements.
                                See <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">Google Ads Privacy</a> for details.</li>
                                <li>In the iOS and Android apps, ad consent in the EEA, UK and Switzerland is collected
                                with Google's consent message (an IAB TCF-registered consent management platform), and
                                on iOS the app also asks for App Tracking Transparency permission before using the
                                advertising identifier.</li>
                            </ul>
                        </li>
                    </ul>
                    <p>
                        You can manage your cookie preferences at any time through the cookie settings banner or
                        your browser settings. Disabling strictly necessary cookies may prevent the Service from
                        functioning properly.
                    </p>
                </section>

                <section>
                    <h2>3. Legal Basis for Processing (GDPR Art. 6)</h2>
                    <p>We process your personal data on the following legal bases:</p>
                    <ul>
                        <li><strong>Contract performance (Art. 6(1)(b)):</strong> Processing necessary to provide the Service,
                        manage your account, process purchases, and deliver purchased digital content (hints, ad removal, streak freezes).</li>
                        <li><strong>Legitimate interest (Art. 6(1)(f)):</strong> Processing for security, fraud prevention,
                        service improvement, and analytics. Our legitimate interest is balanced against your rights and
                        does not override your fundamental rights and freedoms.</li>
                        <li><strong>Legal obligation (Art. 6(1)(c)):</strong> Processing required for tax compliance (VAT
                        records, invoicing), financial reporting, and responding to lawful requests from authorities.</li>
                        <li><strong>Consent (Art. 6(1)(a)):</strong> Processing of advertising cookies, marketing
                        communications, and push notifications. You can withdraw consent at any time without affecting
                        the lawfulness of processing based on consent before its withdrawal.</li>
                    </ul>
                </section>

                <section>
                    <h2>4. How We Use Your Information</h2>
                    <ul>
                        <li><strong>Provide and Maintain Services:</strong> To operate the game, process transactions,
                        and provide customer support.</li>
                        <li><strong>Account Management:</strong> To create and manage your user account, authenticate users,
                        and prevent fraud.</li>
                        <li><strong>Game Features:</strong> To save your progress, maintain leaderboards, track statistics,
                        and enable social features.</li>
                        <li><strong>Purchases and VAT Compliance:</strong> To process payments, issue invoices/receipts,
                        and comply with EU VAT obligations under the OSS scheme.</li>
                        <li><strong>Communications:</strong> To send you game updates, push notifications, and respond to
                        your inquiries (you can opt out at any time).</li>
                        <li><strong>Analytics and Improvements:</strong> To analyze usage patterns, identify bugs, improve
                        game performance, and develop new features.</li>
                        <li><strong>Advertising:</strong> To display advertisements through Google AdSense (web)
                        and AdMob (mobile), only with your consent.</li>
                        <li><strong>Legal Compliance:</strong> To comply with legal obligations, enforce our terms, and
                        protect our rights and the safety of our users.</li>
                    </ul>
                </section>

                <section>
                    <h2>5. Information Sharing and Disclosure</h2>
                    <p>We do not sell your personal information. We may share your information in the following circumstances:</p>

                    <h3>5.1 Service Providers (Data Processors)</h3>
                    <p>We share information with third-party service providers who process data on our behalf under
                    data processing agreements:</p>
                    <ul>
                        <li><strong>Payment Processors:</strong> Stripe (web payments), Apple App Store, Google Play Store</li>
                        <li><strong>Cloud Infrastructure:</strong> Hosting and database services</li>
                        <li><strong>Authentication Services:</strong> Google, Facebook, Apple, X/Twitter for social login</li>
                        <li><strong>Advertising Networks:</strong> Google AdSense, Google AdMob (only with your consent)</li>
                    </ul>

                    <h3>5.2 Legal Requirements</h3>
                    <p>We may disclose your information if required by law or in response to valid legal processes,
                    such as subpoenas, court orders, or government requests.</p>

                    <h3>5.3 Business Transfers</h3>
                    <p>If we are involved in a merger, acquisition, or sale of assets, your information may be
                    transferred as part of that transaction. We will notify you before your data is transferred
                    and becomes subject to a different privacy policy.</p>
                </section>

                <section>
                    <h2>6. Data Retention</h2>
                    <p>
                        We retain your personal information only for as long as necessary to fulfill the purposes
                        outlined in this Privacy Policy:
                    </p>
                    <ul>
                        <li><strong>Account Data:</strong> Retained until you delete your account</li>
                        <li><strong>Game Progress:</strong> Retained while your account is active</li>
                        <li><strong>Transaction Records:</strong> Retained for 11 years as required by Croatian and EU
                        tax law (financial records retention obligation)</li>
                        <li><strong>IP Address Logs:</strong> Retained for up to 12 months for security purposes</li>
                        <li><strong>Anonymous/Aggregated Analytics:</strong> May be retained indefinitely (not personal data)</li>
                    </ul>
                    <p>
                        When you delete your account, we erase your personal data within 30 days, except for data
                        we are legally required to retain (e.g., transaction records for tax compliance).
                    </p>
                </section>

                <section>
                    <h2>7. Your Rights Under GDPR</h2>
                    <p>
                        As a user in the EU/EEA, you have the following rights under the General Data Protection
                        Regulation (GDPR). To exercise any of these rights, contact us at support@word-labyrinth.com.
                        We will respond within 30 days.
                    </p>

                    <h3>7.1 Right of Access (Art. 15)</h3>
                    <p>You have the right to obtain confirmation of whether we process your personal data and to
                    receive a copy of that data.</p>

                    <h3>7.2 Right to Rectification (Art. 16)</h3>
                    <p>You can update your account information through account settings, or contact us to correct
                    inaccurate data.</p>

                    <h3>7.3 Right to Erasure / Right to Be Forgotten (Art. 17)</h3>
                    <p>You can request deletion of your personal data. We will erase your data unless we have a
                    legal obligation to retain it (e.g., transaction records for tax purposes). You can delete your
                    account through the app settings or by contacting us.</p>

                    <h3>7.4 Right to Restriction of Processing (Art. 18)</h3>
                    <p>You can request that we restrict the processing of your data in certain circumstances
                    (e.g., while we verify the accuracy of your data).</p>

                    <h3>7.5 Right to Data Portability (Art. 20)</h3>
                    <p>You can request a copy of your personal data in a structured, commonly used,
                    machine-readable format (JSON). Use the data export feature in your account settings or
                    contact us.</p>

                    <h3>7.6 Right to Object (Art. 21)</h3>
                    <p>You can object to processing based on legitimate interest. We will cease processing
                    unless we demonstrate compelling legitimate grounds that override your interests.</p>

                    <h3>7.7 Right to Withdraw Consent (Art. 7(3))</h3>
                    <p>Where we process data based on your consent (advertising cookies, push notifications),
                    you can withdraw consent at any time. This does not affect the lawfulness of processing
                    before withdrawal. On the website, use <strong>Cookie Settings</strong> in the footer or menu;
                    in the apps, use <strong>Ad Privacy Choices</strong> in the menu or in Settings. On iOS you can
                    also turn off tracking in the device's Settings → Privacy &amp; Security → Tracking.</p>

                    <h3>7.8 Right to Lodge a Complaint</h3>
                    <p>
                        If you believe your data protection rights have been violated, you have the right to lodge
                        a complaint with a supervisory authority. Our lead supervisory authority is:
                    </p>
                    <div className="contact-info">
                        <p><strong>Agencija za zaštitu osobnih podataka (AZOP)</strong></p>
                        <p>Croatian Personal Data Protection Agency</p>
                        <p>Selska cesta 136, 10000 Zagreb, Croatia</p>
                        <p><a href="https://azop.hr" target="_blank" rel="noopener noreferrer">https://azop.hr</a></p>
                    </div>
                    <p>
                        You may also lodge a complaint with the supervisory authority in your country of residence.
                    </p>
                </section>

                <section>
                    <h2>8. Additional Rights for Non-EU Users</h2>

                    <h3>8.1 California Residents (CCPA)</h3>
                    <p>
                        California residents have the right to know what personal information we collect, request
                        deletion of your information, and opt out of sales. We do not sell personal information.
                    </p>
                </section>

                <section>
                    <h2>9. Children's Privacy</h2>
                    <p>
                        Word Labyrinth is not directed to children under the age of 13 (or 16 in the EU). We do not
                        knowingly collect personal information from children. If you believe we have collected information
                        from a child, please contact us immediately, and we will delete such information.
                    </p>
                </section>

                <section>
                    <h2>10. Security</h2>
                    <p>
                        We implement appropriate technical and organizational security measures (Art. 32 GDPR) to
                        protect your information:
                    </p>
                    <ul>
                        <li>Encryption of data in transit (HTTPS/TLS)</li>
                        <li>Encrypted storage of passwords (hashing)</li>
                        <li>Regular security assessments and updates</li>
                        <li>Access controls and authentication</li>
                        <li>Secure payment processing through PCI-DSS compliant providers</li>
                        <li>Database backups with encryption</li>
                    </ul>
                    <p>
                        In the event of a personal data breach that poses a risk to your rights and freedoms,
                        we will notify the relevant supervisory authority within 72 hours and notify affected
                        users without undue delay, as required by GDPR Articles 33 and 34.
                    </p>
                </section>

                <section>
                    <h2>11. International Data Transfers</h2>
                    <p>
                        Word Labyrinth is operated from Croatia, a member state of the European Union. Your information
                        is primarily processed within the EU/EEA. Some of our service providers may process data outside
                        the EU/EEA. We ensure appropriate safeguards are in place for each transfer:
                    </p>
                    <ul>
                        <li><strong>Google</strong> (Analytics, AdSense, AdMob, FCM, OAuth): Data may be processed in
                        the United States. Transfer basis: EU-US Data Privacy Framework (adequacy decision, Art. 45 GDPR).</li>
                        <li><strong>Stripe</strong> (payment processing): Data may be processed in the United States.
                        Transfer basis: EU-US Data Privacy Framework and Standard Contractual Clauses (Art. 46(2)(c) GDPR).</li>
                        <li><strong>Apple</strong> (Sign In with Apple, In-App Purchases, APNs): Data may be processed
                        in the United States. Transfer basis: Standard Contractual Clauses (Art. 46(2)(c) GDPR).</li>
                        <li><strong>Meta/Facebook</strong> (social login): Data may be processed in the United States.
                        Transfer basis: EU-US Data Privacy Framework and Standard Contractual Clauses.</li>
                    </ul>
                    <p>
                        You can request copies of the relevant safeguard documents by contacting us
                        at <a href="mailto:support@word-labyrinth.com">support@word-labyrinth.com</a>.
                    </p>
                </section>

                <section>
                    <h2>12. Third-Party Services</h2>
                    <p>Our game integrates with third-party services that have their own privacy policies:</p>
                    <ul>
                        <li><strong>Google:</strong> OAuth, AdSense, AdMob. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
                        <li><strong>Facebook:</strong> Social login. <a href="https://www.facebook.com/privacy/explanation" target="_blank" rel="noopener noreferrer">Facebook Privacy Policy</a></li>
                        <li><strong>Apple:</strong> Sign In with Apple, In-App Purchases. <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer">Apple Privacy Policy</a></li>
                        <li><strong>X/Twitter:</strong> Social login. <a href="https://twitter.com/en/privacy" target="_blank" rel="noopener noreferrer">X Privacy Policy</a></li>
                        <li><strong>Stripe:</strong> Payment processing. <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a></li>
                    </ul>
                    <p>We encourage you to review the privacy policies of these third-party services.</p>
                </section>

                <section>
                    <h2>13. Changes to This Privacy Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time. We will notify you of material changes by:
                    </p>
                    <ul>
                        <li>Posting the updated policy with a new "Last Updated" date</li>
                        <li>Sending an email notification (if you have an account)</li>
                        <li>Displaying a prominent notice in the game</li>
                    </ul>
                    <p>
                        For material changes that affect the legal basis of processing or introduce new purposes,
                        we will seek your renewed consent where required.
                    </p>
                </section>

                <section>
                    <h2>14. Contact Us</h2>
                    <p>
                        If you have questions, concerns, or requests regarding this Privacy Policy or our data practices,
                        please contact us:
                    </p>
                    <div className="contact-info">
                        <p><strong>Company:</strong> CoreDL d.o.o.</p>
                        <p><strong>OIB:</strong> 33905700350</p>
                        <p><strong>Address:</strong> Gajnički vidikovac II. 14/2, 10090 Zagreb, Croatia</p>
                        <p><strong>Email:</strong> support@word-labyrinth.com</p>
                        <p><strong>Website:</strong> <a href="https://www.word-labyrinth.com" target="_blank" rel="noopener noreferrer">www.word-labyrinth.com</a></p>
                    </div>
                    <p>
                        We will respond to your inquiry within 30 days in accordance with GDPR requirements.
                    </p>
                </section>

                <section className="legal-footer">
                    <p>
                        By using Word Labyrinth, you acknowledge that you have read and understood this Privacy Policy.
                    </p>
                </section>
            </div>
        </div>
    );
}

export default PrivacyPolicy;
